import React, { useState, useEffect, useRef } from 'react';
import { FaBuilding, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaImage, FaDollarSign, FaMoneyBillWave, FaFileInvoice, FaPlus, FaTrash } from 'react-icons/fa';
import * as submasterService from '../../services/submaster.service';
import * as companyService from '../../services/company.service';
import { useCompany } from '../../context/CompanyContext';
import './CompanyDetails.css';

const CompanyDetails = () => {
  const { updateCompany } = useCompany();
  const [branches, setBranches] = useState(() => [companyService.emptyBranch('Branch 1', 'init-1')]);
  const [activeBranchIndex, setActiveBranchIndex] = useState(0);
  const activeIdxRef = useRef(0);
  const nextBankIdRef = useRef(1);

  useEffect(() => {
    activeIdxRef.current = activeBranchIndex;
  }, [activeBranchIndex]);

  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const patchActiveBranch = (updater) => {
    setBranches((prev) => {
      const i = activeIdxRef.current;
      const next = [...prev];
      const cur = next[i];
      if (!cur) return prev;
      next[i] = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater };
      return next;
    });
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContactNo = (contactNo) => {
    const digitsOnly = contactNo.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan);
  };

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst) && gst.length === 15;
  };

  const validateIfscCode = (ifsc) => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc) && ifsc.length === 11;
  };

  const validateAccountNumber = (account) => {
    const digitsOnly = account.replace(/\D/g, '');
    return digitsOnly.length >= 8 && digitsOnly.length <= 18;
  };

  const validateBranch = (b) => {
    const errors = {};
    if (b.contactNo1 && !validateContactNo(b.contactNo1)) {
      errors.contactNo1 = 'Contact number must be 10 digits';
    }
    if (b.contactNo2 && !validateContactNo(b.contactNo2)) {
      errors.contactNo2 = 'Contact number must be 10 digits';
    }
    if (b.email && !validateEmail(b.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (b.panNo && !validatePAN(b.panNo)) {
      errors.panNo = 'PAN must be in format: AAAAA1234A';
    }
    if (b.gstNo && !validateGST(b.gstNo)) {
      errors.gstNo = 'GST must be 15 digits in valid format';
    }
    if (b.bankDetails && Array.isArray(b.bankDetails)) {
      b.bankDetails.forEach((bank, idx) => {
        if (bank.ifscCode && !validateIfscCode(bank.ifscCode)) {
          errors[`bankIfsc_${idx}`] = 'IFSC Code must be 11 digits';
        }
        if (bank.account && !validateAccountNumber(bank.account)) {
          errors[`bankAccount_${idx}`] = 'Account number must be 8-18 digits';
        }
      });
    }
    return errors;
  };

  const validateAllBranches = () => {
    for (let i = 0; i < branches.length; i++) {
      const errs = validateBranch(branches[i]);
      if (Object.keys(errs).length > 0) {
        setActiveBranchIndex(i);
        setValidationErrors(errs);
        return false;
      }
    }
    setValidationErrors({});
    return true;
  };

  // Load currencies and company details
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load currencies
        const currencyData = await submasterService.getItems('currency');
        const currencyList = Array.isArray(currencyData) ? currencyData : currencyData.items || [];
        setCurrencies(currencyList.filter(c => c.active !== false));

        const companyData = await companyService.getCompanyDetails();
        if (companyData?.branches?.length) {
          setBranches(companyData.branches);
          setActiveBranchIndex(0);
          let maxBank = 0;
          companyData.branches.forEach((br) => {
            (br.bankDetails || []).forEach((row) => {
              const n = typeof row.id === 'number' ? row.id : 0;
              if (n > maxBank) maxBank = n;
            });
          });
          nextBankIdRef.current = maxBank + 1;
        }
        
        setLoading(false);
      } catch (e) {
        console.error('Failed to load data', e);
        setError('Failed to load company details');
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    patchActiveBranch({ [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    patchActiveBranch({ companyLogo: file });
    const reader = new FileReader();
    reader.onloadend = () => {
      const url = reader.result;
      setBranches((prev) => {
        const i = activeIdxRef.current;
        const next = [...prev];
        if (!next[i]) return prev;
        next[i] = { ...next[i], logoPreview: url };
        return next;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleBankChange = (id, e) => {
    const { name, value } = e.target;
    patchActiveBranch((b) => ({
      ...b,
      bankDetails: b.bankDetails.map((bank) => (bank.id === id ? { ...bank, [name]: value } : bank)),
    }));
  };

  const addBankDetail = () => {
    patchActiveBranch((b) => {
      if (b.bankDetails.length >= 2) return b;
      const newBank = {
        id: nextBankIdRef.current++,
        bankName: '',
        branchName: '',
        ifscCode: '',
        account: '',
        address: '',
        swiftCode: '',
        currencyId: '',
      };
      return { ...b, bankDetails: [...b.bankDetails, newBank] };
    });
  };

  const removeBankDetail = (id) => {
    patchActiveBranch((b) => ({
      ...b,
      bankDetails: b.bankDetails.filter((bank) => bank.id !== id),
    }));
  };

  const handleIntermediaryChange = (e) => {
    const { name, value } = e.target;
    patchActiveBranch((b) => ({
      ...b,
      intermediaryBank: { ...b.intermediaryBank, [name]: value },
    }));
  };

  const addBranch = () => {
    const next = [
      ...branches,
      companyService.emptyBranch(`Branch ${branches.length + 1}`, `k-${Date.now()}`),
    ];
    setBranches(next);
    setActiveBranchIndex(next.length - 1);
  };

  const removeBranch = () => {
    if (branches.length <= 1) return;
    const idx = activeBranchIndex;
    const next = branches.filter((_, i) => i !== idx);
    setBranches(next);
    setActiveBranchIndex(Math.min(idx, next.length - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAllBranches()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);

      const result = await companyService.saveCompanyDetails(branches);

      if (result.data) {
        const normalized = companyService.normalizeCompanyPayload(result.data);
        setBranches(normalized.branches);
        updateCompany(normalized);
        setSuccessMessage(result.message || 'Company details saved successfully!');
        setValidationErrors({});
      }
    } catch (error) {
      console.error('Error saving company details:', error);
      setError(error.response?.data?.message || 'Failed to save company details. Please try again.');
    }
  };

  const currentBranch = branches[activeBranchIndex];

  return (
    <div className="page-gradient">
      <div className="container-lg mt-2">
        {/* Error and Success Messages */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
          </div>
        )}

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading company details...</p>
          </div>
        )}

        {!loading && currentBranch && (
        <>
        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="company-header-icon">
              <FaBuilding size={28} />
            </div>
            <div>
              <h1 className="mb-0 page-title">Company Details</h1>
              <p className="mb-0 text-muted">Manage your organization's information</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <ul className="nav nav-tabs mb-3 flex-wrap border-0 gap-1">
            {branches.map((br, i) => (
              <li className="nav-item" key={br.localKey}>
                <button
                  type="button"
                  className={`nav-link rounded-3 ${i === activeBranchIndex ? 'active fw-semibold' : ''}`}
                  onClick={() => setActiveBranchIndex(i)}
                >
                  {br.label || `Branch ${i + 1}`}
                </button>
              </li>
            ))}
          </ul>
          <div className="d-flex flex-wrap gap-2 mb-4">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={addBranch}>
              <FaPlus className="me-1" /> Add branch
            </button>
            {branches.length > 1 && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeBranch}>
                <FaTrash className="me-1" /> Remove this branch
              </button>
            )}
          </div>

          {/* Basic Information Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-primary">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 company-card-title-primary">
                <FaBuilding className="me-2" />
                Basic Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label htmlFor="branchLabel" className="form-label fw-600 text-dark-700">Branch tab name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="branchLabel"
                    name="label"
                    value={currentBranch.label}
                    onChange={handleChange}
                    placeholder="e.g. Head Office, Branch 2"
                    required
                  />
                </div>
                <div className="col-lg-6 mb-3">
                  <label htmlFor="companyName" className="form-label fw-600 text-dark-700">Company Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="companyName"
                    name="companyName"
                    value={currentBranch.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    required
                  />
                </div>
                <div className="col-lg-6 mb-3">
                  <div className="d-flex gap-3 align-items-start">
                    <div className="flex-1">
                      <label htmlFor="companyLogo" className="form-label fw-600 text-dark-700">Company Logo</label>
                      <div className="input-group">
                        <span className="input-group-text input-muted-bg">
                          <FaImage />
                        </span>
                        <input
                          type="file"
                          className="form-control"
                          id="companyLogo"
                          name="companyLogo"
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </div>
                    </div>
                    {currentBranch.logoPreview && (
                      <div className="mt-32">
                        <div className="logo-preview">
                          <img src={currentBranch.logoPreview} alt="Company Logo Preview" className="logo-preview-img" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 mb-3">
                  <label htmlFor="address" className="form-label fw-600 text-dark-700">Address <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control form-control-lg"
                    id="address"
                    name="address"
                    value={currentBranch.address}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Enter full address"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-green">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 company-card-title-green">
                <FaMapMarkerAlt className="me-2" />
                Location Details
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-3 mb-3">
                  <label htmlFor="country" className="form-label fw-600 text-dark-700">Country <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="country"
                    name="country"
                    value={currentBranch.country}
                    onChange={handleChange}
                    placeholder="Country"
                    required
                  />
                </div>
                <div className="col-lg-3 mb-3">
                  <label htmlFor="state" className="form-label fw-600 text-dark-700">State <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="state"
                    name="state"
                    value={currentBranch.state}
                    onChange={handleChange}
                    placeholder="State"
                    required
                  />
                </div>
                <div className="col-lg-3 mb-3">
                  <label htmlFor="city" className="form-label fw-600 text-dark-700">City <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="city"
                    name="city"
                    value={currentBranch.city}
                    onChange={handleChange}
                    placeholder="City"
                    required
                  />
                </div>
                <div className="col-lg-3 mb-3">
                  <label htmlFor="pincode" className="form-label fw-600 text-dark-700">Pincode <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="pincode"
                    name="pincode"
                    value={currentBranch.pincode}
                    onChange={handleChange}
                    placeholder="Pincode"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-red">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 company-card-title-red">
                <FaPhone className="me-2" />
                Contact Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label htmlFor="contactNo1" className="form-label fw-600 text-dark-700">Contact No 1 <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    className={`form-control form-control-lg ${validationErrors.contactNo1 ? 'is-invalid' : ''}`}
                    id="contactNo1"
                    name="contactNo1"
                    value={currentBranch.contactNo1}
                    onChange={handleChange}
                    placeholder="+91 xxxxxxxxxx"
                    required
                  />
                  {validationErrors.contactNo1 && (
                    <div className="invalid-feedback d-block">{validationErrors.contactNo1}</div>
                  )}
                </div>
                <div className="col-lg-6 mb-3">
                  <label htmlFor="contactNo2" className="form-label fw-600 text-dark-700">Contact No 2</label>
                  <input
                    type="tel"
                    className={`form-control form-control-lg ${validationErrors.contactNo2 ? 'is-invalid' : ''}`}
                    id="contactNo2"
                    name="contactNo2"
                    value={currentBranch.contactNo2}
                    onChange={handleChange}
                    placeholder="+91 xxxxxxxxxx"
                  />
                  {validationErrors.contactNo2 && (
                    <div className="invalid-feedback d-block">{validationErrors.contactNo2}</div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label htmlFor="email" className="form-label fw-600 text-dark-700">Email <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text input-muted-bg">
                      <FaEnvelope />
                    </span>
                    <input
                      type="email"
                      className={`form-control form-control-lg ${validationErrors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      value={currentBranch.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                  {validationErrors.email && (
                    <div className="invalid-feedback d-block">{validationErrors.email}</div>
                  )}
                </div>
                <div className="col-lg-6 mb-3">
                  <label htmlFor="website" className="form-label fw-600 text-dark-700">Website</label>
                  <div className="input-group">
                    <span className="input-group-text input-muted-bg">
                      <FaGlobe />
                    </span>
                    <input
                      type="url"
                      className="form-control form-control-lg"
                      id="website"
                      name="website"
                      value={currentBranch.website}
                      onChange={handleChange}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Government Details Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-amber">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 company-card-title-amber">
                <FaFileInvoice className="me-2" />
                Registered Details
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-4 mb-3">
                  <label htmlFor="panNo" className="form-label fw-600 text-dark-700">PAN No <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${validationErrors.panNo ? 'is-invalid' : ''}`}
                    id="panNo"
                    name="panNo"
                    value={currentBranch.panNo}
                    onChange={handleChange}
                    placeholder="XXXXXXXXXX"
                    required
                  />
                  {validationErrors.panNo && (
                    <div className="invalid-feedback d-block">{validationErrors.panNo}</div>
                  )}
                </div>
                <div className="col-lg-4 mb-3">
                  <label htmlFor="gstNo" className="form-label fw-600 text-dark-700">GST No <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${validationErrors.gstNo ? 'is-invalid' : ''}`}
                    id="gstNo"
                    name="gstNo"
                    value={currentBranch.gstNo}
                    onChange={handleChange}
                    placeholder="XXXXXXXXXXXXXXXXXXX"
                    required
                  />
                  {validationErrors.gstNo && (
                    <div className="invalid-feedback d-block">{validationErrors.gstNo}</div>
                  )}
                </div>
                <div className="col-lg-4 mb-3">
                  <label htmlFor="regCin" className="form-label fw-600 text-dark-700">REG/CIN <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="regCin"
                    name="regCin"
                    value={currentBranch.regCin}
                    onChange={handleChange}
                    placeholder="XXXXXXXXXXXXXXXX"
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 mb-3">
                  <label htmlFor="ifcCode" className="form-label fw-600 text-dark-700">IFC Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${validationErrors.ifcCode ? 'is-invalid' : ''}`}
                    id="ifcCode"
                    name="ifcCode"
                    value={currentBranch.ifcCode}
                    onChange={handleChange}
                    placeholder="XXXXXXXX"
                    required
                  />
                  {validationErrors.ifcCode && (
                    <div className="invalid-feedback d-block">{validationErrors.ifcCode}</div>
                  )}
                </div>
                <div className="col-lg-4 mb-3">
                  <label htmlFor="lutNo" className="form-label fw-600 text-dark-700">LUT NO. <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="lutNo"
                    name="lutNo"
                    value={currentBranch.lutNo}
                    onChange={handleChange}
                    placeholder="XXXXXXXXXXX"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Banking Details Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-blue">
            <div className="card-header bg-white border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 company-card-title-blue">
                  <FaMoneyBillWave className="me-2" />
                  Banking Details
                </h5>
                {currentBranch.bankDetails.length < 2 && (
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addBankDetail}>
                    <FaPlus className="me-1" /> Add Bank Detail
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              {currentBranch.bankDetails.length === 0 ? (
                <div className="text-center py-5 text-muted-600">
                  <FaMoneyBillWave size={40} className="mb-3 icon-dim" />
                  <p>No bank details added yet</p>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={addBankDetail}>
                    <FaPlus className="me-1" /> Add First Bank Detail
                  </button>
                </div>
              ) : (
                currentBranch.bankDetails.map((bank, idx) => (
                  <div key={bank.id} className={`mb-4 pb-4 ${idx < currentBranch.bankDetails.length - 1 ? 'section-divider' : ''}`}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="company-card-title-blue">Bank {idx + 1}</h6>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBankDetail(bank.id)}>
                        <FaTrash size={14} className="me-1" /> Remove
                      </button>
                    </div>
                    <div className="row">
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-600 text-dark-700">Bank Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="bankName"
                          value={bank.bankName}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          placeholder="Enter bank name"
                          required
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-600 text-dark-700">Branch Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="branchName"
                          value={bank.branchName}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          placeholder="Enter branch name"
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-3 mb-3">
                        <label className="form-label fw-600 text-dark-700">IFSC Code <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className={`form-control ${validationErrors[`bankIfsc_${idx}`] ? 'is-invalid' : ''}`}
                          name="ifscCode"
                          value={bank.ifscCode}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          placeholder="XXXXXXXX"
                          required
                        />
                        {validationErrors[`bankIfsc_${idx}`] && (
                          <div className="invalid-feedback d-block">{validationErrors[`bankIfsc_${idx}`]}</div>
                        )}
                      </div>
                      <div className="col-lg-3 mb-3">
                        <label className="form-label fw-600 text-dark-700">Account <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className={`form-control ${validationErrors[`bankAccount_${idx}`] ? 'is-invalid' : ''}`}
                          name="account"
                          value={bank.account}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          placeholder="Account number"
                          required
                        />
                        {validationErrors[`bankAccount_${idx}`] && (
                          <div className="invalid-feedback d-block">{validationErrors[`bankAccount_${idx}`]}</div>
                        )}
                      </div>
                      <div className="col-lg-3 mb-3">
                        <label className="form-label fw-600 text-dark-700">Swift Code</label>
                        <input
                          type="text"
                          className="form-control"
                          name="swiftCode"
                          value={bank.swiftCode}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          placeholder="XXXXXXXX"
                        />
                      </div>
                      <div className="col-lg-3 mb-3">
                        <label className="form-label fw-600 text-dark-700">Currency <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          name="currencyId"
                          value={bank.currencyId || ''}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          required
                        >
                          <option value="">Select Currency</option>
                          {currencies.map((currency) => (
                            <option key={currency.id} value={currency.id}>
                              {currency.name} ({currency.symbol})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12 mb-3">
                        <label className="form-label fw-600 text-dark-700">Address <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          name="address"
                          value={bank.address}
                          onChange={(e) => handleBankChange(bank.id, e)}
                          rows="2"
                          placeholder="Enter bank address"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Intermediary Bank Section */}
          <div className="card border-0 shadow-sm mb-4 company-card-purple">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 company-card-title-purple">
                <FaDollarSign className="me-2" />
                Intermediatery Bank Detail
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-4 mb-3">
                  <label htmlFor="intermediaryBankName" className="form-label fw-600 text-dark-700">Bank Name</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="intermediaryBankName"
                    name="bankName"
                    value={currentBranch.intermediaryBank.bankName}
                    onChange={handleIntermediaryChange}
                    placeholder="Enter bank name"
                  />
                </div>
                <div className="col-lg-4 mb-3">
                  <label htmlFor="intermediaryRoutedThrough" className="form-label fw-600 text-dark-700">Routed through</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="intermediaryRoutedThrough"
                    name="routedThrough"
                    value={currentBranch.intermediaryBank.routedThrough}
                    onChange={handleIntermediaryChange}
                    placeholder="Routing details"
                  />
                </div>
                <div className="col-lg-4 mb-3">
                  <label htmlFor="intermediaryAccountName" className="form-label fw-600 text-dark-700">Account Name</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="intermediaryAccountName"
                    name="accountName"
                    value={currentBranch.intermediaryBank.accountName}
                    onChange={handleIntermediaryChange}
                    placeholder="Account name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mb-4 text-end">
            <button type="submit" className="btn btn-lg btn-gradient-primary" disabled={loading}>
              Save Changes
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
}

export default CompanyDetails;
